import { useEffect, useMemo, useState } from 'react'
import { format } from 'date-fns'
import { useParams } from 'react-router'
import { useShallow } from 'zustand/react/shallow'

import { STORE_STATUS } from '@/constants/store'
import { useOddsHistoryStore } from '@/stores/golfPricing/oddsHistory.store'
import { usePlayerGroupsStore } from '@/stores/golfPricing/playerGroups.store'
import { useGolfPricingTournamentStore } from '@/stores/golfPricing/tournament.store'

import { OddsDataContent } from './OddsDataContent/OddsDataContent'
import { OddsDataFilters } from './OddsDataFilters/OddsDataFilters'
import { MarketTypes } from './constants'
import { getOddsDataTableRows } from './oddsDataTableRows.utils'
import { OddsFilters, TypeOption } from './types'
import { useTeamRoundWebSocket } from './useTeamRoundWebSocket'

export const OddsDataViewDDE = () => {
  const { tournamentId } = useParams<{ tournamentId: string }>()
  const [filters, setFilters] = useState<OddsFilters>({
    marketType: null,
    players: [],
    startDateTime: null,
    endDateTime: null,
    actionType: []
  })
  const [isActionTypeFilterDisabled, setIsActionTypeFilterDisabled] = useState(true)

  const {
    data: oddsHistory,
    fetchData: fetchOddsHistory,
    isFetching,
    isLoading
  } = useOddsHistoryStore(
    useShallow((state) => ({
      data: state.data,
      isLoading: state.status === STORE_STATUS.loading,
      isFetching: state.status === STORE_STATUS.fetching,
      fetchData: state.fetchData,
      resetState: state.resetState
    }))
  )

  const tournament = useGolfPricingTournamentStore(
    useShallow((state) => {
      const tournamentData =
        Array.isArray(state.data) && state.data.length > 0 ? state.data[0] : null
      return tournamentData
    })
  )

  const tournamentName = tournament?.name || 'Tournament'

  const handleMarketTypeChange = (value: TypeOption | null) => {
    setFilters((prev) => ({
      ...prev,
      marketType: value,
      players: [],
      actionType: []
    }))
    setIsActionTypeFilterDisabled(true)
  }

  const handlePlayersChange = (playerIds: number[]) => {
    setFilters((prev) => ({
      ...prev,
      players: playerIds,
      actionType: []
    }))
    setIsActionTypeFilterDisabled(true)
  }

  const handleStartDateTimeChange = (date: Date | null) => {
    setFilters((prev) => ({
      ...prev,
      startDateTime: date,
      actionType: []
    }))
    setIsActionTypeFilterDisabled(true)
  }

  const handleEndDateTimeChange = (date: Date | null) => {
    setFilters((prev) => ({
      ...prev,
      endDateTime: date,
      actionType: []
    }))
    setIsActionTypeFilterDisabled(true)
  }

  const playersKey = useMemo(() => {
    if (filters.players.length === 0) return ''
    const sorted = [...filters.players].sort((a, b) => a - b)
    return sorted.join(',')
  }, [filters.players])

  const startDateTimeKey = useMemo(
    () => (filters.startDateTime ? filters.startDateTime.getTime() : null),
    [filters.startDateTime]
  )
  const endDateTimeKey = useMemo(
    () => (filters.endDateTime ? filters.endDateTime.getTime() : null),
    [filters.endDateTime]
  )

  useEffect(() => {
    if (
      filters.marketType &&
      filters.players.length > 0 &&
      filters.startDateTime &&
      filters.endDateTime &&
      tournamentId
    ) {
      const { cancelFetch, resetState } = useOddsHistoryStore.getState()
      cancelFetch('Filter changed, fetching new data')
      resetState()

      fetchOddsHistory({
        competitionId: Number(tournamentId),
        playerIds: filters.players,
        marketType: filters.marketType.value,
        timeRange: {
          startTime: format(filters.startDateTime, "yyyy-MM-dd'T'HH:mm:ss"),
          endTime: format(filters.endDateTime, "yyyy-MM-dd'T'HH:mm:ss")
        }
      })
    }
  }, [
    filters.marketType?.value,
    playersKey,
    startDateTimeKey,
    endDateTimeKey,
    tournamentId,
    fetchOddsHistory
  ])

  const areAllFiltersSelected = Boolean(
    filters.marketType && filters.players.length > 0 && filters.startDateTime && filters.endDateTime
  )

  const playerNames = useMemo(
    () => oddsHistory?.[0]?.players?.map((player) => player.playerFullName),
    [oddsHistory]
  )

  const { data: playerGroups } = usePlayerGroupsStore(
    useShallow((state) => ({
      data: state.data
    }))
  )

  const selectedPlayerNames = useMemo(() => {
    if (!filters.players.length || !playerGroups) return []

    if (filters.marketType?.value === MarketTypes.WINNER) {
      const allPlayers = playerGroups.flatMap((group) => group.players)
      return filters.players
        .map((id) => allPlayers.find((p) => p.playerId === id)?.playerFullName)
        .filter((name): name is string => !!name)
    } else {
      const matchingGroup = playerGroups.find(
        (group) =>
          group.players?.length === 3 &&
          group.players.every((p) => filters.players.includes(p.playerId))
      )
      return matchingGroup?.players?.map((p) => p.playerFullName) || []
    }
  }, [filters.players, filters.marketType?.value, playerGroups])

  const { teamRoundActions, isWebSocketLoading, rawDataRef } = useTeamRoundWebSocket({
    areAllFiltersSelected,
    tournamentId,
    tournament,
    filters
  })

  const filteredTeamRoundActions = useMemo(() => {
    if (filters.players.length === 0) {
      return teamRoundActions
    }
    return teamRoundActions.filter((action) => filters.players.includes(action.playerId))
  }, [teamRoundActions, filters.players])

  const tableRows = useMemo(
    () =>
      getOddsDataTableRows({
        oddsHistory,
        marketType: filters.marketType?.value,
        filteredTeamRoundActions,
        playerGroups,
        selectedPlayerIds: filters.players,
        rawDataMap: rawDataRef.current
      }),
    [
      oddsHistory,
      filters.marketType?.value,
      filteredTeamRoundActions,
      playerGroups,
      filters.players
    ]
  )

  const availableActionTypes = useMemo(() => {
    const uniqueActionTypes = new Set<string>()
    uniqueActionTypes.add('Odds Update')

    tableRows.forEach((row) => {
      if (row.actionType) {
        uniqueActionTypes.add(row.actionType)
      }
    })
    const actionTypeOptions: TypeOption[] = Array.from(uniqueActionTypes)
      .sort()
      .map((actionType) => ({ value: actionType, label: actionType }))
    return actionTypeOptions
  }, [tableRows])

  useEffect(() => {
    if (isLoading || isFetching || isWebSocketLoading) {
      setIsActionTypeFilterDisabled(true)
    } else if (tableRows.length > 0) {
      setIsActionTypeFilterDisabled(false)
    }
  }, [tableRows.length, isLoading, isFetching, isWebSocketLoading])

  const filteredTableRows = useMemo(() => {
    let filtered = tableRows

    if (filters.actionType.length > 0) {
      const selectedValues = filters.actionType.map((option) => option.value)
      filtered = filtered.filter((row) => row.actionType && selectedValues.includes(row.actionType))
    }

    if (filters.startDateTime && filters.endDateTime) {
      filtered = filtered.filter((row) => {
        const actionTime = new Date(row.time).getTime()
        const startTime = filters.startDateTime!.getTime()
        const endTime = filters.endDateTime!.getTime()
        return actionTime >= startTime && actionTime <= endTime
      })
    }

    return filtered
  }, [tableRows, filters.actionType, filters.startDateTime, filters.endDateTime])

  return (
    <>
      <OddsDataFilters
        filters={filters}
        onMarketTypeChange={handleMarketTypeChange}
        onPlayersChange={handlePlayersChange}
        onStartDateTimeChange={handleStartDateTimeChange}
        onEndDateTimeChange={handleEndDateTimeChange}
        onActionTypeChange={(value) => {
          setFilters((prev) => ({ ...prev, actionType: value }))
        }}
        actionTypeOptions={availableActionTypes}
        isActionTypeFilterDisabled={isActionTypeFilterDisabled}
      />

      <OddsDataContent
        areAllFiltersSelected={areAllFiltersSelected}
        tableRows={filteredTableRows}
        playerNames={playerNames}
        isLoading={isLoading || isWebSocketLoading}
        isFetching={isFetching || isWebSocketLoading}
        filters={filters}
        tournamentName={tournamentName}
        selectedPlayerNames={selectedPlayerNames}
      />
    </>
  )
}
